import { createEffect, createSignal, on, onMount } from "solid-js";
import type { SidePaneResize } from "./useSidePaneResize";
import "./SidePaneResizer.css";

type Props = {
	resize: SidePaneResize;
	/** Accessible name, saying what is being resized. */
	label: string;
};

/**
 * The grip down a side pane's left edge. An `hr` is the separator between the
 * column and the pane, and a focusable one is how a reader moves it.
 */
export const SidePaneResizer = (props: Props) => {
	// The grip comes and goes with the pane, so it reads its position off the
	// layout once the pane it sits on has been laid out.
	onMount(() => requestAnimationFrame(props.resize.sync));

	// The grip follows the pointer through a drag, so the pointer is still on
	// it when the drag ends; without this it stays lit by :hover until the
	// pointer happens to move off a few pixels' width.
	const [released, setReleased] = createSignal(false);
	createEffect(
		on(
			props.resize.isResizing,
			(resizing, wasResizing) => {
				if (wasResizing && !resizing) {
					setReleased(true);
				}
			},
			{ defer: true },
		),
	);

	return (
		<hr
			class="side-pane-resizer"
			classList={{
				"side-pane-resizer--dragging": props.resize.isResizing(),
				"side-pane-resizer--released": released(),
			}}
			aria-orientation="vertical"
			aria-label={props.label}
			aria-valuenow={props.resize.separatorPosition()}
			tabIndex={0}
			onPointerDown={(event) => {
				setReleased(false);
				props.resize.start(event);
			}}
			onPointerLeave={() => setReleased(false)}
			onKeyDown={props.resize.nudge}
			onDblClick={props.resize.reset}
		/>
	);
};
